import {Card, CardBody, Col, Input, ListGroup, Row} from "reactstrap";
import React, {useContext, useEffect, useRef, useState} from "react";
import axiosConfig from "../../../../Config/axios";
import Loader from "../../../../Layout/Loader";
import {Search} from "react-feather";
import AuthorizationContext from "../../../../_helper/Authorization";
import PrivilegeListPagination from "../../../../CommonElements/Pagination/PrivilegeListPagination";
import type {Role, RolePermissionWithToggle} from "../Types/RoleTypes";
import type {AuthorizationContextType} from "../../../../_helper/Authorization/AuthorizationTypes";

interface RoleListViewComponentProps {
    userID: number;
}

const RoleListView: React.FC<RoleListViewComponentProps> = ({userID}) => {
    const [loaderVisibility, setLoaderVisibility] = useState<boolean>(false);
    const [roles, setRoles] = useState<RolePermissionWithToggle[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(12);
    const [totalRows, setTotalRows] = useState<number>(0);
    const searchTimeoutRef = useRef<number | null>(null);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};

    const normalizeSearchTerm = (value: string) => value.replace(/\s+/g, ' ').trim();

    useEffect(() => {
        if (searchTimeoutRef.current !== null) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = window.setTimeout(() => {
            setDebouncedSearchTerm(normalizeSearchTerm(searchTerm));
        }, 1000);
        return () => {
            if (searchTimeoutRef.current !== null) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, userID]);

    useEffect(() => {
        getRoles(currentPage, perPage, debouncedSearchTerm);
    }, [currentPage, perPage, debouncedSearchTerm, userID]);

    const getRoles = (page: number = currentPage, limit: number = perPage, name: string = debouncedSearchTerm) => {
        setLoaderVisibility(true);
        const searchName = normalizeSearchTerm(name);
        Promise.all([
            axiosConfig.get(`/privilege/role`, {
                params: {
                    user_id: userID,
                    name: searchName || undefined,
                }
            }),
            axiosConfig.get(`/privilege/role`, {
                params: {
                    name: searchName || undefined,
                    page,
                    per_page: limit,
                }
            })
        ])
            .then(([currentUserResponse, roleResponse]) => {
                const userRoleList = currentUserResponse?.data?.data?.data || [];

                if (roleResponse?.data?.data?.data) {
                    setRoleIsOn(roleResponse.data.data.data, userRoleList);
                }

                const meta = roleResponse?.data?.data?.meta;
                if (meta) {
                    setCurrentPage(meta.current_page || page);
                    setPerPage(meta.per_page || limit);
                    setTotalRows(meta.total || 0);
                }
                setLoaderVisibility(false);
            })
            .catch(exception => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    };

    const setRoleIsOn = (rolesList: Role[], userRolesList: Role[]) => {
        const rolesData = rolesList.map(role => ({
            ...role, isOn: !!userRolesList.find((currentRole) => currentRole.id === role.id)
        }));
        setRoles(rolesData);
    }

    const handleToggle = (roleId: number, event: React.ChangeEvent<HTMLInputElement>) => {
        setLoaderVisibility(true);
        axiosConfig
            .get(`privilege/user/${roleId}/role`, {
                params: {
                    'user_id': userID, 'type': event?.target?.checked ? 'attach' : 'detach'
                }
            })
            .then(response => {
                if (response?.data?.data) {
                    setRoleIsOn(roles, response?.data?.data);
                }
                setLoaderVisibility(false);
            })
            .catch(exception => {
                errorHandler(exception);
                setLoaderVisibility(false);
            });
    };

    return (<div>
        <Loader visibility={loaderVisibility}/>
        <CardBody>
            <div className="job-filter">
                <div className="faq-form">
                    <Input className="form-control" type="text" placeholder="Search.."
                           value={searchTerm}
                           onChange={(event) => setSearchTerm(event.target.value)}/>
                    <Search className="search-icon"/>
                    <div className="categories">
                        <ListGroup>
                            <Row>
                                {roles.map((role, key) => (<Col sm="4" key={key}>
                                    <Card>
                                        <CardBody style={{height: '150px'}}
                                                  className="shadow-lg p-25 shadow-showcase text-center">
                                            <h6>
                                                {role.display_name}
                                            </h6>
                                            <label className="switch form-label">
                                                <input type="checkbox"
                                                       checked={role.isOn || false}
                                                       onChange={(event) => handleToggle(role.id, event)}/>
                                                <span className="switch-state"></span>
                                            </label>
                                        </CardBody>
                                    </Card>
                                </Col>))}
                            </Row>
                        </ListGroup>
                        <PrivilegeListPagination
                            currentPage={currentPage}
                            perPage={perPage}
                            totalRows={totalRows}
                            visibleCount={roles.length}
                            onPageChange={setCurrentPage}
                        />
                    </div>
                </div>
            </div>
        </CardBody>
    </div>);
}
export default RoleListView;